import { useCallback, useState } from 'react';

import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron-icon.svg';
import { ReactComponent as BackIcon } from '../../assets/icons/collection-back-icon.svg';

import store, { useAppSelector } from 'store';
import { toggleCollectionQuestionAnswer } from 'store/gameSlice';

import styled from 'styled-components';

const CollectionComponent = () => {
  const collectionData = useAppSelector((state) => state.game.collectionData);
  const [selectedQuestion, setSelectedQuestion] = useState<{
    index: number;
  } | null>(null);

  const handleResetSelectedQuestion = useCallback(() => {
    setSelectedQuestion(null);
  }, []);

  return collectionData ? (
    <PageWrap>
      <PageContent>
        <Header>
          {!!selectedQuestion && (
            <BackBtn onClick={handleResetSelectedQuestion}>
              <BackIcon />
            </BackBtn>
          )}
          <p>
            {selectedQuestion
              ? `Question ${selectedQuestion.index + 1}`
              : `Answer Key: ${collectionData.name}`}
          </p>
        </Header>
        <Main>
          {selectedQuestion ? (
            <SelectedQuestionWrap>
              <QuestionHeader>Question</QuestionHeader>
              <QuestionContent>
                {
                  collectionData.questions[selectedQuestion.index].question
                    .content
                }
              </QuestionContent>
              <QuestionHeader>Answer</QuestionHeader>
              <AnswersList>
                {new Array(8).fill(null).map((_, index) => (
                  <AnswerItem
                    key={index}
                    grayed={
                      !!collectionData.questions[selectedQuestion.index]
                        .question.answers[index]?.isRevealed
                    }
                    onClick={() =>
                      store.dispatch(
                        toggleCollectionQuestionAnswer({
                          questionIndex: selectedQuestion.index,
                          index,
                        }),
                      )
                    }
                  >
                    <AnswerIndex>{index + 1}.</AnswerIndex>
                    <AnswerContent>
                      {collectionData.questions[selectedQuestion.index].question
                        .answers[index]?.answer || ''}
                    </AnswerContent>
                    <AnswerPoints>
                      {Number.isFinite(
                        collectionData.questions[selectedQuestion.index]
                          .question.answers[index]?.point,
                      )
                        ? collectionData.questions[selectedQuestion.index]
                            .question.answers[index]?.point
                        : ''}
                    </AnswerPoints>
                  </AnswerItem>
                ))}
              </AnswersList>
            </SelectedQuestionWrap>
          ) : (
            <QuestionsList>
              {collectionData.questions.map((question, index) => (
                <QuestionItem
                  key={index}
                  onClick={() => setSelectedQuestion({ index })}
                >
                  <span>{index + 1}.</span>
                  <p>{question.question.content}</p>
                  <ChevronIcon />
                </QuestionItem>
              ))}
            </QuestionsList>
          )}
        </Main>
      </PageContent>
    </PageWrap>
  ) : null;
};

const PageWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 100svw;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: flex-start;
  height: 100svh;
  background: #f2f3f7;
  & * {
    box-sizing: border-box;
  }
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 500px;
  background: #fff;
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.header`
  position: relative;
  width: 100%;
  padding: 1.5rem;
  border-bottom: 0.125rem solid #f2f3f7;
  color: #252636;
  font-family: LexendDeca;
  font-weight: 600;
  font-size: 1.125rem;
  line-height: normal;
  text-transform: capitalize;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 1.25rem;
`;

const BackBtn = styled.button`
  position: relative;
  display: flex;
  cursor: pointer;
  outline: none;
  border: none;
  padding: 0;
  background: none;
`;

const Main = styled.main`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 0.5rem;
    height: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background: #fafafa;
  }

  &::-webkit-scrollbar-thumb {
    border: 0.125rem solid #fafafa;
    border-radius: 0.25rem;
    opacity: 0.4;
    background: #9ba1b9;
  }
`;

const QuestionsList = styled.ul`
  position: relative;
  width: 100%;
  list-style: none;
  padding: 1.5rem;
  display: flex;
  flex-flow: column nowrap;
  gap: 1.25rem;
  margin: 0;
`;

const QuestionItem = styled.li`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0;
  cursor: pointer;
  color: #252636;
  font-family: LexendDeca;
  font-size: 1rem;
  line-height: 1.4;
  gap: 0.625rem;
  text-transform: uppercase;

  & > p {
    flex-grow: 1;
    text-align: left;
  }

  & > svg {
    align-self: center;
    min-width: 1rem;
    width: 1rem;
    aspect-ratio: 1/1;
    transform: rotate(-90deg);

    & > path {
      fill: #252636;
    }
  }
`;

const SelectedQuestionWrap = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.5rem 0.75rem;
  color: #252636;
  font-family: LexendDeca;
  font-size: 1rem;
  line-height: 1.4;
`;

const QuestionHeader = styled.h2`
  font-size: 1rem;
  margin: 0;
`;

const QuestionContent = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const AnswersList = styled.ul`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 1rem;
  margin: 0;
  padding: 0;
`;

const AnswerItem = styled.li<{ grayed: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  gap: 0.5rem;
  min-height: 2.375rem;
  opacity: ${({ grayed }) => (grayed ? 0.5 : 1)};
  cursor: pointer;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0;
    background: #f2f3f7;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 400;
    text-transform: uppercase;
    color: #252636;
  }
`;

const AnswerIndex = styled.span`
  text-align: center;
  min-width: 1.5625rem;
`;

const AnswerContent = styled.p`
  flex-grow: 1;
  justify-content: flex-start;
  padding: 0 0.75rem;
`;

const AnswerPoints = styled.p`
  min-width: 3.125rem;
  text-align: center;
`;

export default CollectionComponent;
